<template>
  <div>
    <b-modal
      id="bynder-image-editor-publish-modal"
      no-close-on-backdrop
      title="Publish to Bynder"
      centered
      size="lg"
      @shown="handleModalShown"
      @hide="handleModalHide"
    >
      <div class="text-center">
        <div
          v-if="fileName"
          class="mt-3"
        >
          <h5>Publish: <small class="text-muted">{{ fileName }}</small></h5>
        </div>
      </div>

      <b-card class="mb-3">
        <b-form>
          <b-row>
            <b-col cols="4">
              <b-form-group
                label="Platform"
                label-for="platform-select"
                label-class="mb-1"
              >
                <b-form-select
                  id="platform-select"
                  v-model="platformId"
                  size="sm"
                >
                  <b-form-select-option
                    value=""
                    disabled
                    selected
                  >
                    Select Platform
                  </b-form-select-option>
                  <b-form-select-option
                    v-for="platform in platforms"
                    :key="platform.id"
                    :value="platform.id"
                  >
                    {{ platform.name }}
                  </b-form-select-option>
                </b-form-select>
              </b-form-group>
            </b-col>

            <b-col cols="4">
              <b-form-group
                label="DPI"
                label-for="dpi-select"
                label-class="mb-1"
              >
                <b-form-select
                  id="dpi-select"
                  v-model="dpi"
                  size="sm"
                >
                  <b-form-select-option value="72">
                    72
                  </b-form-select-option>
                  <b-form-select-option value="75">
                    75
                  </b-form-select-option>
                  <b-form-select-option value="100">
                    100
                  </b-form-select-option>
                  <b-form-select-option value="144">
                    144
                  </b-form-select-option>
                  <b-form-select-option value="150">
                    150
                  </b-form-select-option>
                  <b-form-select-option value="200">
                    200
                  </b-form-select-option>
                  <b-form-select-option value="300">
                    300
                  </b-form-select-option>
                </b-form-select>
              </b-form-group>
            </b-col>

            <b-col cols="4">
              <b-form-group
                label="Format"
                label-for="format-select"
                label-class="mb-1"
              >
                <b-form-select
                  id="format-select"
                  v-model="imageType"
                  size="sm"
                >
                  <b-form-select-option value="jpeg">
                    JPG
                  </b-form-select-option>
                  <b-form-select-option value="png">
                    PNG
                  </b-form-select-option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </b-card>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content-end">
          <b-button
            class="mr-2"
            variant="secondary"
            @click="closeModal"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            :disabled="isPublishing || !platformId"
            @click="handlePublish"
          >
            <b-spinner
              v-if="isPublishing"
              small
              class="mr-1"
            />
            {{ isPublishing ? 'Publishing...' : 'Publish to Bynder' }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { convertDataUrlToFile } from '@frontend/group/modules/publish-utils/publish-utils';
import { mapGetters } from 'vuex';

export default {
    name: 'BynderPublishModal',

    props: {
        canvas: {
            type: Object,
            required: false,
            default: null
        }
    },

    data: () => ({
        file: null,
        fileName: '',
        isPublishing: false,
        platformId: '',
        dpi: '72',
        imageType: 'jpeg',
        platforms: [],
    }),

    computed: {
        ...mapGetters('fileBrowser', [
            'firstSelectedItem',
        ]),
    },

    methods: {
        handleModalShown() {
            this.fileName = this.firstSelectedItem?.name ?? this.canvas?.getDownloadFileName() ?? 'untitled';

            this.getPlatforms();
        },

        handleModalHide() {
            this.resetModal();
        },

        closeModal() {
            this.$bvModal.hide('bynder-image-editor-publish-modal');
        },

        async getPlatforms() {
            const { data } = await axios.get('/bynder/get-platforms');
            this.platforms = data.platforms;
        },

        async handlePublish() {
            if (!this.canvas) {
                toastr.error('No canvas available to publish.');

                return;
            }

            if (!this.platformId) {
                toastr.error('Please select a platform.');

                return;
            }

            let fileName = this.fileName + '.' + this.imageType;

            const canvas = this.canvas.canvas;

            const tempCanvas = document.createElement('canvas');
            const ctx = tempCanvas.getContext('2d');
            const canvasElement = canvas.getElement();

            tempCanvas.width = canvas.width;
            tempCanvas.height = canvas.height;

            if (this.imageType === 'jpeg') {
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            }

            ctx.drawImage(canvasElement, 0, 0, tempCanvas.width, tempCanvas.height);

            const dataUrl = tempCanvas.toDataURL(`image/${this.imageType}`, 0.8);

            const file = await convertDataUrlToFile(dataUrl, fileName, this.dpi, this.imageType);

            const formData = new FormData();

            formData.append('file', file);
            formData.append('image_type', this.imageType);
            formData.append('dpi', this.dpi);
            formData.append('platform_id', this.platformId);

            try {
                this.isPublishing = true;

                const { data } = await axios.post('/bynder/file-upload', formData);

                if (data.status === 'error') {
                    toastr.error(data.message);
                } else {
                    toastr.success(data.message);
                }
            } catch (error) {
                console.log('Error: ', error);
            } finally {
                this.isPublishing = false;
            }
        },

        resetModal() {
            this.fileName = '';
            this.isPublishing = false;
            this.platformId = '';
            this.dpi = '72';
            this.imageType = 'jpeg';
        }
    }
}
</script>
